import React, { useState, useEffect } from 'react';
import { okulEkle, okulListele, okulSil, okulGuncelle, logListele } from '../services/apiService';
import { HiOfficeBuilding, HiChartBar, HiPlus, HiPencil, HiTrash, HiX, HiDownload, HiCheckCircle } from 'react-icons/hi';
import { MdSchool } from 'react-icons/md';
import toast, { Toaster } from 'react-hot-toast';
import './SuperAdminPage.css';

const SuperAdminPage = () => {
  const [activeTab, setActiveTab] = useState('okullar');
  const [okullar, setOkullar] = useState([]);
  const [loglar, setLoglar] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOkul, setEditingOkul] = useState(null);

  const LOGS_PER_PAGE = 30;

  const [formData, setFormData] = useState({
    okulAdi: '',
    yetkiliAdi: '',
    yetkiliKullaniciAdi: '',
    yetkiliSifre: ''
  });

  const [editFormData, setEditFormData] = useState({
    okulAdi: '',
    yetkiliAdi: '',
    yetkiliKullaniciAdi: '',
    mevcutSifre: '',
    yetkiliSifre: ''
  });

  useEffect(() => {
    if (activeTab === 'okullar') {
      loadOkullar();
    } else if (activeTab === 'loglar') {
      loadLoglar();
    }
  }, [activeTab]);

  const loadOkullar = async () => {
    setLoading(true);
    try {
      const response = await okulListele();
      if (response.success) {
        setOkullar(response.okullar || []);
      }
    } catch (err) {
      toast.error('Okul listesi alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const loadLoglar = async () => {
    setLoading(true);
    try {
      const response = await logListele();
      if (response.success) {
        setLoglar(response.loglar || []);
      }
    } catch (err) {
      toast.error('Log listesi alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const loadingToast = toast.loading('Okul ekleniyor...');
    try {
      const response = await okulEkle(formData);

      if (response.success) {
        toast.success('Okul başarıyla eklendi!', { id: loadingToast });
        setFormData({
          okulAdi: '',
          yetkiliAdi: '',
          yetkiliKullaniciAdi: '',
          yetkiliSifre: ''
        });
        setShowAddModal(false);
        loadOkullar();
      } else {
        toast.error(response.mesaj || 'Okul eklenemedi', { id: loadingToast });
      }
    } catch (err) {
      toast.error(err.message || 'Bir hata oluştu', { id: loadingToast });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (okulId, okulAdi) => {
    if (!window.confirm(`${okulAdi} okulunu silmek istediğinize emin misiniz?`)) {
      return;
    }

    const loadingToast = toast.loading('Siliniyor...');
    try {
      const response = await okulSil(okulId);
      if (response.success) {
        toast.success('Okul silindi', { id: loadingToast });
        loadOkullar();
      } else {
        toast.error(response.mesaj || 'Okul silinemedi', { id: loadingToast });
      }
    } catch (err) {
      toast.error(err.message || 'Bir hata oluştu', { id: loadingToast });
    }
  };

  const handleEdit = (okul) => {
    setEditingOkul(okul);
    setEditFormData({
      okulAdi: okul.okulAdi,
      yetkiliAdi: '',
      yetkiliKullaniciAdi: '',
      mevcutSifre: '',
      yetkiliSifre: ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const loadingToast = toast.loading('Güncelleniyor...');
    try {
      const response = await okulGuncelle({
        okulId: editingOkul.okulId,
        ...editFormData
      });

      if (response.success) {
        toast.success('Okul güncellendi!', { id: loadingToast });
        setShowEditModal(false);
        setEditingOkul(null);
        loadOkullar();
      } else {
        toast.error(response.mesaj || 'Güncelleme başarısız', { id: loadingToast });
      }
    } catch (err) {
      toast.error(err.message || 'Bir hata oluştu', { id: loadingToast });
    } finally {
      setLoading(false);
    }
  };

  const formatTarih = (tarih) => {
    if (!tarih) return '-';
    const tarihStr = tarih.endsWith('Z') ? tarih : tarih + 'Z';
    return new Date(tarihStr).toLocaleString('tr-TR', {
      timeZone: 'Europe/Istanbul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionText = (action) => {
    const actionMap = {
      'okul_eklendi': 'Okul Eklendi',
      'okul_silindi': 'Okul Silindi',
      'veli_eklendi': 'Veli Eklendi',
      'veli_kayit': 'Veli Kaydedildi',
      'veli_silindi': 'Veli Silindi',
      'veli_guncellendi': 'Veli Güncellendi',
      'yuz_tanindi': 'Yüz Tanındı',
      'yuz_tanima': 'Yüz Tanındı',
      'yuz_taninamadi': 'Yüz Tanınamadı',
      'login': 'Giriş Yapıldı'
    };
    return actionMap[action] || action;
  };

  const totalPages = Math.ceil(loglar.length / LOGS_PER_PAGE);
  const startIndex = (currentPage - 1) * LOGS_PER_PAGE;
  const endIndex = startIndex + LOGS_PER_PAGE;
  const currentLogs = loglar.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const exportToExcel = () => {
    const headers = ['Tarih', 'İşlem Tipi', 'Detaylar', 'Okul ID', 'Kullanıcı ID'];
    const rows = loglar.map(log => [
      formatTarih(log.tarih),
      getActionText(log.islemTipi).replace(/[^\w\s-]/g, ''),
      log.detaylar,
      log.okulId,
      log.kullaniciId
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `sistem-loglari-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Excel dosyası indirildi!');
  };

  return (
    <div className="super-admin-panel">
      <Toaster position="top-right" />

      {/* Header with Stats */}
      <div className="panel-header">
        <div className="header-content">
          <h1 className="header-title">
            <MdSchool className="header-icon" />
            Super Admin Panel
          </h1>
          <p className="header-subtitle">Sistem Yönetimi ve İzleme</p>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon stat-icon-primary">
              <HiOfficeBuilding />
            </div>
            <div className="stat-content">
              <p className="stat-label">Toplam Okul</p>
              <p className="stat-value">{okullar.length}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon stat-icon-success">
              <HiChartBar />
            </div>
            <div className="stat-content">
              <p className="stat-label">Toplam Log</p>
              <p className="stat-value">{loglar.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'okullar' ? 'active' : ''}`}
          onClick={() => setActiveTab('okullar')}
        >
          <HiOfficeBuilding />
          Okullar ({okullar.length})
        </button>
        <button
          className={`tab ${activeTab === 'loglar' ? 'active' : ''}`}
          onClick={() => setActiveTab('loglar')}
        >
          <HiChartBar />
          Sistem Logları
        </button>
      </div>

      {/* Okullar Tab */}
      {activeTab === 'okullar' && (
        <div className="content-card animate-slideUp">
          <div className="card-header">
            <h2 className="card-title">Kayıtlı Okullar</h2>
            <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
              <HiPlus /> Yeni Okul Ekle
            </button>
          </div>

          {loading ? (
            <div className="empty-state">
              <div className="spinner"></div>
              <p>Yükleniyor...</p>
            </div>
          ) : okullar.length === 0 ? (
            <div className="empty-state">
              <HiOfficeBuilding className="empty-icon" />
              <h3>Henüz kayıtlı okul yok</h3>
              <p>Yeni okul ekleyerek başlayın</p>
            </div>
          ) : (
            <div className="okul-grid">
              {okullar.map((okul) => (
                <div key={okul.okulId} className="okul-card">
                  <div className="okul-card-header">
                    <div className="okul-avatar">
                      <MdSchool />
                    </div>
                    <div className="okul-info">
                      <h3 className="okul-name">{okul.okulAdi}</h3>
                      <p className="okul-collection">Collection: {okul.collectionId?.substring(0, 12)}...</p>
                    </div>
                  </div>
                  <div className="okul-card-body">
                    <div className="info-row">
                      <span className="info-label">URL:</span>
                      <a href="https://yuztanimasistemi.net" target="_blank" rel="noopener noreferrer" className="info-link">
                        yuztanimasistemi.net
                      </a>
                    </div>
                    {okul.kayitTarihi && (
                      <div className="info-row">
                        <span className="info-label">Kayıt:</span>
                        <span className="info-value">{formatTarih(okul.kayitTarihi)}</span>
                      </div>
                    )}
                  </div>
                  <div className="okul-card-actions">
                    <button onClick={() => handleEdit(okul)} className="btn btn-sm btn-secondary">
                      <HiPencil /> Düzenle
                    </button>
                    <button onClick={() => handleDelete(okul.okulId, okul.okulAdi)} className="btn btn-sm btn-danger">
                      <HiTrash /> Sil
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Loglar Tab */}
      {activeTab === 'loglar' && (
        <div className="content-card animate-slideUp">
          <div className="card-header">
            <h2 className="card-title">Sistem Aktivite Logları</h2>
            {loglar.length > 0 && (
              <button className="btn btn-secondary" onClick={exportToExcel}>
                <HiDownload /> Excel'e Aktar
              </button>
            )}
          </div>

          {loading ? (
            <div className="empty-state">
              <div className="spinner"></div>
              <p>Yükleniyor...</p>
            </div>
          ) : loglar.length === 0 ? (
            <div className="empty-state">
              <HiChartBar className="empty-icon" />
              <h3>Henüz log kaydı yok</h3>
            </div>
          ) : (
            <>
              <div className="log-table-wrapper">
                <table className="log-table">
                  <thead>
                    <tr>
                      <th>Tarih</th>
                      <th>Aksiyon</th>
                      <th>Detaylar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentLogs.map((log) => (
                      <tr key={log.logId}>
                        <td>{formatTarih(log.tarih)}</td>
                        <td>
                          <span className="log-badge">{getActionText(log.islemTipi)}</span>
                        </td>
                        <td>{log.detaylar}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    ← Önceki
                  </button>

                  <div className="page-numbers">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let page = i + 1;
                      if (totalPages > 5) {
                        if (currentPage > 3) {
                          page = currentPage - 2 + i;
                        }
                        if (page > totalPages) return null;
                      }
                      return (
                        <button
                          key={page}
                          className={`btn btn-sm ${page === currentPage ? 'btn-primary' : 'btn-secondary'}`}
                          onClick={() => goToPage(page)}
                        >
                          {page}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Sonraki →
                  </button>

                  <span className="page-info">
                    Sayfa {currentPage} / {totalPages} ({loglar.length} kayıt)
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content animate-scaleIn" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                <HiPlus />
                Yeni Okul Ekle
              </h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>
                <HiX />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modern-form">
              <div className="form-group">
                <label className="form-label">Okul Adı *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.okulAdi}
                  onChange={(e) => setFormData({ ...formData, okulAdi: e.target.value })}
                  placeholder="Örn: Mavi Anaokulu"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Yetkili Adı Soyadı *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.yetkiliAdi}
                  onChange={(e) => setFormData({ ...formData, yetkiliAdi: e.target.value })}
                  placeholder="Örn: Ayşe Yılmaz"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Kullanıcı Adı *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.yetkiliKullaniciAdi}
                  onChange={(e) => setFormData({ ...formData, yetkiliKullaniciAdi: e.target.value })}
                  placeholder="Örn: mavi.yonetici"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Şifre *</label>
                <input
                  type="password"
                  className="form-input"
                  value={formData.yetkiliSifre}
                  onChange={(e) => setFormData({ ...formData, yetkiliSifre: e.target.value })}
                  placeholder="Güvenli bir şifre"
                  required
                  minLength={6}
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <div className="spinner-sm"></div>
                      <span>Kaydediliyor...</span>
                    </>
                  ) : (
                    <>
                      <HiCheckCircle />
                      <span>Okul Ekle</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingOkul && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content animate-scaleIn" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                <HiPencil />
                Okul Düzenle
              </h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>
                <HiX />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="modern-form">
              <div className="form-group">
                <label className="form-label">Okul Adı *</label>
                <input
                  type="text"
                  className="form-input"
                  value={editFormData.okulAdi}
                  onChange={(e) => setEditFormData({ ...editFormData, okulAdi: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Yetkili Adı (opsiyonel)</label>
                <input
                  type="text"
                  className="form-input"
                  value={editFormData.yetkiliAdi}
                  onChange={(e) => setEditFormData({ ...editFormData, yetkiliAdi: e.target.value })}
                  placeholder="Değiştirmek istiyorsanız girin"
                />
                <small className="form-hint">Boş bırakırsanız değiştirilmez</small>
              </div>

              <div className="form-group">
                <label className="form-label">Kullanıcı Adı (opsiyonel)</label>
                <input
                  type="text"
                  className="form-input"
                  value={editFormData.yetkiliKullaniciAdi}
                  onChange={(e) => setEditFormData({ ...editFormData, yetkiliKullaniciAdi: e.target.value })}
                  placeholder="Değiştirmek istiyorsanız girin"
                />
                <small className="form-hint">Boş bırakırsanız değiştirilmez</small>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <div className="spinner-sm"></div>
                      <span>Güncelleniyor...</span>
                    </>
                  ) : (
                    <>
                      <HiCheckCircle />
                      <span>Güncelle</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminPage;
